import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Save, Zap } from "lucide-react";

const PLANS = [["essencial", "Essencial"], ["avancado", "Avançado"], ["consultoria", "Consultoria"]];
const n = (v) => (Number(v) || 0).toLocaleString("pt-BR");

export default function AdminAi() {
    const [cfg, setCfg] = useState(null);
    const [an, setAn] = useState(null);
    const [grant, setGrant] = useState({ account_id: "", tokens: 0, queries: 0 });

    const load = () => {
        api.get("/admin/ai/config").then((r) => setCfg(r.data)).catch(() => {});
        api.get("/admin/ai/analytics").then((r) => setAn(r.data)).catch(() => {});
    };
    useEffect(load, []);

    if (!cfg) return <div className="p-6 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

    const models = cfg.models || {};
    const limits = cfg.plan_limits || {};
    const setModel = (k, v) => setCfg({ ...cfg, models: { ...models, [k]: v } });
    const setLimit = (plan, field, v) => setCfg({ ...cfg, plan_limits: { ...limits, [plan]: { ...(limits[plan] || {}), [field]: Number(v) } } });
    const setPrice = (m, field, v) => setCfg({ ...cfg, pricing: { ...cfg.pricing, [m]: { ...(cfg.pricing[m] || {}), [field]: Number(v) } } });

    const saveConfig = async () => {
        try {
            await api.put("/admin/ai/config", { enabled: cfg.enabled, models: cfg.models,
                max_output_tokens: Number(cfg.max_output_tokens), plan_limits: cfg.plan_limits });
            toast.success("Configuração de IA salva.");
        } catch (e) { toast.error("Erro ao salvar."); }
    };
    const savePrice = async (m) => {
        const p = cfg.pricing[m] || {};
        try {
            await api.put("/admin/ai/pricing", { model: m, input_price_per_million: Number(p.input),
                cached_input_price_per_million: Number(p.cached_input || 0), output_price_per_million: Number(p.output) });
            toast.success(`Preço de ${m} atualizado.`);
        } catch (e) { toast.error("Erro ao salvar preço."); }
    };
    const doGrant = async () => {
        if (!grant.account_id) return toast.error("Informe o account_id.");
        try {
            await api.post("/admin/ai/grant", { account_id: grant.account_id, tokens: Number(grant.tokens), queries: Number(grant.queries) });
            toast.success("Créditos concedidos.");
            setGrant({ account_id: "", tokens: 0, queries: 0 });
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro."); }
    };

    const inp = "border border-slate-200 rounded-sm px-2 py-1 text-sm w-full";
    const card = "rounded-lg border border-slate-200 bg-white p-4";

    return (
        <div className="space-y-4" data-testid="admin-ai">
            {/* Consumo/custo */}
            <div className="grid sm:grid-cols-2 gap-4">
                {an && [["Hoje", an.today], ["Este mês", an.month]].map(([label, d]) => (
                    <div key={label} className={card} data-testid={`admin-ai-metrics-${label === "Hoje" ? "today" : "month"}`}>
                        <h4 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-1.5"><Zap className="w-4 h-4 text-mineral" />{label}</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><div className="text-slate-400 text-xs">Consultas</div><div className="font-semibold">{n(d.queries)}</div></div>
                            <div><div className="text-slate-400 text-xs">Custo (USD)</div><div className="font-semibold">${(d.cost_usd || 0).toFixed(4)}</div></div>
                            <div><div className="text-slate-400 text-xs">Tokens entrada</div><div className="font-semibold">{n(d.tokens_in)}</div></div>
                            <div><div className="text-slate-400 text-xs">Tokens saída</div><div className="font-semibold">{n(d.tokens_out)}</div></div>
                        </div>
                    </div>
                ))}
            </div>
            {an && (
                <div className="grid md:grid-cols-2 gap-4">
                    {[["Por modelo", an.by_model], ["Por plano", an.by_plan], ["Top clientes", an.top_clients], ["Top processos", an.top_processes]].map(([label, rows]) => (
                        <div key={label} className={card}>
                            <h4 className="text-sm font-bold text-slate-800 mb-2">{label}</h4>
                            <table className="w-full text-xs">
                                <tbody className="divide-y divide-slate-100">
                                    {(rows || []).map((r, i) => (
                                        <tr key={i}><td className="py-1 text-slate-600">{r.key || "—"}</td>
                                            <td className="py-1 text-right text-slate-500">{n(r.queries)} cons.</td>
                                            <td className="py-1 text-right text-slate-500">{n(r.total_tokens)} tok</td>
                                            <td className="py-1 text-right font-medium">${(r.cost_usd || 0).toFixed(4)}</td></tr>
                                    ))}
                                    {(!rows || !rows.length) && <tr><td className="py-1 text-slate-400">Sem dados</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    ))}
                    <div className={card}>
                        <h4 className="text-sm font-bold text-slate-800 mb-2">Médias (mês)</h4>
                        <p className="text-sm text-slate-600">Tokens por consulta: <b>{n(an.avg_tokens_per_query)}</b></p>
                        <p className="text-sm text-slate-600">Custo médio por consulta: <b>${(an.avg_cost_per_query || 0).toFixed(6)}</b></p>
                    </div>
                </div>
            )}

            {/* Configuração */}
            <div className={card}>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-slate-800">Configuração do Assistente</h4>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="admin-ai-enabled" />
                        Assistente {cfg.enabled ? "ativado" : "desativado"}
                    </label>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mb-3">
                    {[["fast", "Modelo rápido"], ["standard", "Modelo padrão"], ["advanced", "Modelo avançado"]].map(([k, label]) => (
                        <div key={k}>
                            <label className="text-xs text-slate-500">{label}</label>
                            <select className={inp} value={models[k] || ""} onChange={(e) => setModel(k, e.target.value)} data-testid={`admin-ai-model-${k}`}>
                                {(cfg.available_models || []).map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                    ))}
                </div>
                <div className="mb-3">
                    <label className="text-xs text-slate-500">Máximo de tokens de saída por consulta (max_tokens_per_request)</label>
                    <input type="number" className={`${inp} sm:w-48`} value={cfg.max_output_tokens} onChange={(e) => setCfg({ ...cfg, max_output_tokens: e.target.value })} data-testid="admin-ai-maxout" />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    {PLANS.map(([code, label]) => (
                        <div key={code} className="border border-slate-100 rounded-sm p-2">
                            <div className="text-xs font-semibold text-slate-600 mb-1">{label}</div>
                            <label className="text-[11px] text-slate-400">Consultas/mês</label>
                            <input type="number" className={inp} value={(limits[code] || {}).queries ?? ""} placeholder="padrão do plano" onChange={(e) => setLimit(code, "queries", e.target.value)} data-testid={`admin-ai-limit-${code}-queries`} />
                            <label className="text-[11px] text-slate-400">Tokens/mês</label>
                            <input type="number" className={inp} value={(limits[code] || {}).tokens ?? ""} placeholder="padrão do plano" onChange={(e) => setLimit(code, "tokens", e.target.value)} data-testid={`admin-ai-limit-${code}-tokens`} />
                        </div>
                    ))}
                </div>
                <button onClick={saveConfig} className="mt-3 bg-petroleum text-white rounded-sm px-4 py-2 text-sm font-semibold inline-flex items-center gap-2" data-testid="admin-ai-save"><Save className="w-4 h-4" />Salvar configuração</button>
            </div>

            {/* Preços dos modelos */}
            <div className={card}>
                <h4 className="text-sm font-bold text-slate-800 mb-2">Preços dos modelos (USD por 1 milhão de tokens)</h4>
                <div className="space-y-2">
                    {Object.keys(cfg.pricing || {}).map((m) => {
                        const p = cfg.pricing[m] || {};
                        return (
                            <div key={m} className="grid grid-cols-5 gap-2 items-end">
                                <div className="col-span-2 text-xs text-slate-600 truncate">{m}</div>
                                <div><label className="text-[10px] text-slate-400">Entrada</label><input type="number" step="0.01" className={inp} value={p.input ?? ""} onChange={(e) => setPrice(m, "input", e.target.value)} /></div>
                                <div><label className="text-[10px] text-slate-400">Saída</label><input type="number" step="0.01" className={inp} value={p.output ?? ""} onChange={(e) => setPrice(m, "output", e.target.value)} /></div>
                                <button onClick={() => savePrice(m)} className="border border-slate-300 rounded-sm px-2 py-1 text-xs hover:bg-slate-50">Salvar</button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Créditos extras */}
            <div className={card}>
                <h4 className="text-sm font-bold text-slate-800 mb-2">Conceder pacote adicional (créditos do ciclo atual)</h4>
                <div className="grid sm:grid-cols-4 gap-2 items-end">
                    <div className="sm:col-span-2"><label className="text-[11px] text-slate-400">account_id do cliente</label><input className={inp} value={grant.account_id} onChange={(e) => setGrant({ ...grant, account_id: e.target.value })} data-testid="admin-ai-grant-account" /></div>
                    <div><label className="text-[11px] text-slate-400">+ Consultas</label><input type="number" className={inp} value={grant.queries} onChange={(e) => setGrant({ ...grant, queries: e.target.value })} data-testid="admin-ai-grant-queries" /></div>
                    <div><label className="text-[11px] text-slate-400">+ Tokens</label><input type="number" className={inp} value={grant.tokens} onChange={(e) => setGrant({ ...grant, tokens: e.target.value })} data-testid="admin-ai-grant-tokens" /></div>
                </div>
                <button onClick={doGrant} className="mt-3 bg-mineral text-white rounded-sm px-4 py-2 text-sm font-semibold" data-testid="admin-ai-grant-btn">Conceder créditos</button>
            </div>
        </div>
    );
}
